import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import DialogCloseButton from "../buttons/DialogCloseButton";
import {EmergencyContact} from "../../models/emergencyContact";
import MessageBox, {MessageType} from "../components/MessageBox";
import {UserRole} from "../../models/account";
import {Tour} from "../../models/tour";
import {createTourMembership, updateTourMembership} from "../../api/membershipTourApi";
import {TourMembership} from "../../models/membership";
import {formatDateTime} from "../../utils/formatDateTime";
import {useRequiredAccount} from "../../hooks/AccountContext";

interface FormData {
    shareContactData: boolean;
    shareMedData: boolean;
    shareEmergencyContacts: EmergencyContact[];
}

type Props = {
    membership?: TourMembership | null;
    tour: Tour;
    accountEmergencyContacts: EmergencyContact[];
    onClose: () => void;
    onSaved: () => void;
};

export function TourMembershipDialog({
                                         membership,
                                         tour,
                                         accountEmergencyContacts,
                                         onClose,
                                         onSaved
                                     }: Props) {
    const {t} = useTranslation();

    const {
        account,
        contactData: accountContactData,
        medData: accountMedData,
    } = useRequiredAccount();

    const [formData, setFormData] = useState<FormData>({
        shareContactData: false,
        shareMedData: false,
        shareEmergencyContacts: []
    });

    const [message, setMessage] = useState<MessageType>(null);
    const [disableSaving, setDisableSaving] = useState<boolean>(!!membership);

    // 🔥 FIX: Effekt reagiert jetzt auf ALLE relevanten Daten
    useEffect(() => {
        let emergencyContacts = membership ? [...membership.shareEmergencyContacts] : [];

        accountEmergencyContacts.forEach(c => {
            if (c.shareData && !emergencyContacts.some(ec => ec.id === c.id)) {
                emergencyContacts.push(c);
            }
        });

        setFormData({
            shareContactData: membership ? membership.shareContactData : (accountContactData ? accountContactData.shareData : false),
            shareMedData: membership ? membership.shareMedicalData : (accountMedData ? accountMedData.shareData : false),
            shareEmergencyContacts: emergencyContacts
        });

        setDisableSaving(!!membership);
    }, [
        membership,
        accountEmergencyContacts,
        accountContactData,
        accountMedData
    ]);

    const handleEmergenyContactSelectionChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        emergencyContact: EmergencyContact
    ) => {
        const checked = e.target.checked;

        setFormData(prev => ({
            ...prev,
            shareEmergencyContacts: checked
                ? [...prev.shareEmergencyContacts, emergencyContact]
                : prev.shareEmergencyContacts.filter(c => c.id !== emergencyContact.id)
        }));

        setDisableSaving(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked, type} = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : e.target.value
        }));
        setDisableSaving(false);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setMessage(null);
        try {
            if (!membership) {
                const dto = {
                    role: UserRole.APPLIED,
                    shareContactData: formData.shareContactData,
                    shareMedicalData: formData.shareMedData,
                    shareEmergencyContacts: formData.shareEmergencyContacts.map(c => c.id)
                };
                await createTourMembership(account.id, tour.id, dto);
                onClose();
            } else {
                const dto = {
                    role: membership.role,
                    shareContactData: formData.shareContactData,
                    shareMedicalData: formData.shareMedData,
                    shareEmergencyContacts: formData.shareEmergencyContacts.map(c => c.id)
                };
                await updateTourMembership(account.id, tour.id, dto);
            }
            setMessage({type: "success", text: t("common.messages.success")});
            setDisableSaving(true);
            onSaved();
        } catch {
            setMessage({type: "error", text: t("common.messages.error")});
        }
    };

    return (
        <div className="dialog-overlay">
            <div className="dialog">
                <DialogCloseButton onClose={onClose}/>
                <>
                    <h3>{t("tours.buttons.editMembership")}</h3>
                    <h4>{tour.destination}</h4>
                    <h4>{`${formatDateTime(tour.validFrom)} - ${formatDateTime(tour.validTo)}`}</h4>
                </>

                <form onSubmit={handleSubmit} className="form-container">
                    <MessageBox message={message}/>

                    {/* Share Contact Data */}
                    <label>
                        <input
                            type="checkbox"
                            name="shareContactData"
                            checked={formData.shareContactData}
                            disabled={!accountContactData || accountContactData.shareData}
                            onChange={handleChange}
                            title={!accountContactData ? t("tours.membership.requestDialog.disableShowContactData") : (accountContactData.shareData ? t("tours.membership.requestDialog.disableHideContactData") : undefined)}
                        />
                        {t("contact.fields.shareContactData")}
                    </label>

                    {/* Share Medical Data */}
                    <label>
                        <input
                            type="checkbox"
                            name="shareMedData"
                            checked={formData.shareMedData}
                            disabled={!accountMedData || accountMedData.shareData}
                            onChange={handleChange}
                            title={!accountMedData ? t("tours.membership.requestDialog.disableShowMedData") : (accountMedData.shareData ? t("tours.membership.requestDialog.disableHideMedData") : undefined)}
                        />
                        {t("medical.fields.shareMedData")}
                    </label>

                    {/* Notfallkontakte */}
                    <div className="form-group">
                        <h4>{t("emergency.fields.emergencyContactList")}</h4>

                        {accountEmergencyContacts.map((emergencyContact, index) => (
                            <div key={index}>
                                <input
                                    type="checkbox"
                                    name="shareEmergencyContactData"
                                    value={emergencyContact.id}
                                    disabled={emergencyContact.shareData}
                                    checked={formData.shareEmergencyContacts.some(c => c.id === emergencyContact.id)}
                                    onChange={(e) => handleEmergenyContactSelectionChange(e, emergencyContact)}
                                    title={emergencyContact.shareData ? t("tours.membership.requestDialog.disableHideEmergencyContact") : undefined}
                                />
                                {emergencyContact.lastName}, {emergencyContact.firstName}
                            </div>
                        ))}
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={disableSaving}
                        className="form-button">
                        {t("common.button.save")}
                    </button>
                </form>

            </div>
        </div>
    );
}
